import { useEffect, useRef, useCallback } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from './firebase-config';

interface InactivityTimeoutOptions {
  timeoutMinutes?: number;
  warningMinutes?: number;
  onWarning?: () => void;
  onTimeout?: () => void;
  enabled?: boolean;
}

/**
 * Hook to automatically sign out users after a period of inactivity
 * 
 * @param options Configuration options
 * @param options.timeoutMinutes Minutes of inactivity before auto-logout (default: 30)
 * @param options.warningMinutes Minutes before timeout to show warning (default: 5)
 * @param options.onWarning Callback when warning should be shown
 * @param options.onTimeout Callback when timeout occurs (before signout)
 * @param options.enabled Whether inactivity tracking is enabled (default: true)
 */
export const useInactivityTimeout = ({
  timeoutMinutes = 30,
  warningMinutes = 5,
  onWarning,
  onTimeout,
  enabled = true
}: InactivityTimeoutOptions = {}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
  }, []);

  const handleTimeout = useCallback(async () => {
    console.log('⏰ Inactivity timeout - signing out user');
    
    // Call custom timeout callback if provided
    if (onTimeout) {
      onTimeout();
    }
    
    try {
      await signOut(auth);
      auth.tenantId = null; // Reset tenant ID after sign out
      
      // Show alert after signout
      alert('You have been signed out due to inactivity for security purposes.');
      
      // Try to close window (will only work if opened by script)
      try {
        window.close();
      } catch (e) {
        console.log('Cannot close window - not opened by script');
      }
    } catch (error) {
      console.error('Error during inactivity timeout signout:', error);
    }
  }, [onTimeout]);

  const handleWarning = useCallback(() => {
    console.log('⚠️ Inactivity warning - user will be signed out soon');
    
    if (onWarning) {
      onWarning();
    }
  }, [onWarning]);

  const resetInactivityTimer = useCallback(() => {
    if (!enabled) return;

    lastActivityRef.current = Date.now();
    clearTimers();

    // Set warning timer
    const warningTime = (timeoutMinutes - warningMinutes) * 60 * 1000;
    if (warningTime > 0) {
      warningRef.current = setTimeout(handleWarning, warningTime);
    }

    // Set timeout timer
    const timeoutTime = timeoutMinutes * 60 * 1000;
    timeoutRef.current = setTimeout(handleTimeout, timeoutTime);
  }, [enabled, timeoutMinutes, warningMinutes, handleWarning, handleTimeout, clearTimers]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    // Start the timer
    resetInactivityTimer();

    // Activity event listeners
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'wheel'
    ];

    // Add throttling to prevent excessive timer resets
    let throttleTimeout: NodeJS.Timeout | null = null;
    const throttledReset = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          resetInactivityTimer();
          throttleTimeout = null;
        }, 1000); // Throttle to once per second
      }
    };

    events.forEach(event => {
      window.addEventListener(event, throttledReset);
    });

    // Cleanup
    return () => {
      clearTimers();
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
      events.forEach(event => {
        window.removeEventListener(event, throttledReset);
      });
    };
  }, [enabled, resetInactivityTimer, clearTimers]);

  // Return method to manually reset timer (useful for keeping session alive during active operations)
  return {
    resetTimer: resetInactivityTimer,
    clearTimers,
    getLastActivityTime: () => lastActivityRef.current
  };
};
